import { useState } from "react";
import {
  Card,
  DatePicker,
  Flex,
  Layout,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import dayjs, { Dayjs } from "dayjs";
import {
  BotActionLogInterface,
  BotActionStatus,
  BotActionType,
  useGetBotActionLogQuery,
} from "api";
import { dateTimeFormat, format, palette } from "libs";
import { Put } from "components";

interface BotActivityTabProps {
  botId: number;
}

const ACTION_TYPE_OPTIONS = Object.entries(BotActionType).map(([key, value]) => ({
  value,
  label: key,
}));

const STATUS_OPTIONS: {
  value: BotActionStatus;
  label: string;
  color: string;
}[] = [
  { value: BotActionStatus.SUCCESS, label: "Succes", color: "green" },
  { value: BotActionStatus.FAILED, label: "Esuat", color: "red" },
  { value: BotActionStatus.SKIPPED, label: "Sarit", color: "default" },
  {
    value: BotActionStatus.RATE_LIMITED,
    label: "Rate-limited",
    color: "orange",
  },
];

export const BotActivityTab = ({ botId }: BotActivityTabProps) => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [actionType, setActionType] = useState<BotActionType | undefined>();
  const [status, setStatus] = useState<BotActionStatus | undefined>();
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const { data, isLoading, isFetching } = useGetBotActionLogQuery({
    botId,
    page,
    per_page: perPage,
    action_type: actionType,
    status,
    since: range?.[0] ? range[0].toISOString() : undefined,
    until: range?.[1] ? range[1].toISOString() : undefined,
  });

  const columns: ColumnsType<BotActionLogInterface> = [
    {
      title: "Cand",
      dataIndex: "executed_at",
      width: 170,
      render: (executed_at: string | null) =>
        executed_at ? format(executed_at, dateTimeFormat) : "—",
    },
    {
      title: "Tip",
      dataIndex: "action_type",
      render: (t: string) => <Tag>{t}</Tag>,
    },
    {
      title: "Tinta",
      render: (_, log) => (
        <Put condition={!!log.target_type}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {log.target_type}#{log.target_id}
          </Typography.Text>
        </Put>
      ),
    },
    {
      title: "Status",
      dataIndex: ["status", "value"],
      width: 130,
      render: (value: BotActionStatus) => {
        const cfg = STATUS_OPTIONS.find((s) => s.value === value);
        return cfg ? <Tag color={cfg.color}>{cfg.label}</Tag> : "—";
      },
    },
    {
      title: "Latenta",
      dataIndex: "latency_ms",
      align: "right" as const,
      width: 100,
      render: (ms: number | null) => (ms != null ? `${ms} ms` : "—"),
    },
    {
      title: "Eroare",
      render: (_, log) => (
        <Put condition={!!log.error_code || !!log.error_message}>
          <Tooltip title={log.error_message || ""}>
            <Typography.Text
              type="danger"
              style={{ fontSize: 12, fontFamily: "monospace" }}
            >
              {log.error_code}
            </Typography.Text>
          </Tooltip>
        </Put>
      ),
    },
    {
      title: "Task",
      dataIndex: "task_id",
      align: "right" as const,
      width: 80,
      render: (id: number | null) => (id ? `#${id}` : "—"),
    },
  ];

  return (
    <>
      <Card style={{ marginBottom: 16 }}>
        <Flex justify="space-between" wrap="wrap" gap={8}>
          <Space wrap>
            <Select
              placeholder="Tip actiune"
              style={{ width: 200 }}
              allowClear
              value={actionType}
              onChange={(v) => {
                setActionType(v);
                setPage(1);
              }}
              options={ACTION_TYPE_OPTIONS}
              showSearch
              optionFilterProp="label"
            />
            <Select
              placeholder="Status"
              style={{ width: 160 }}
              allowClear
              value={status}
              onChange={(v) => {
                setStatus(v);
                setPage(1);
              }}
              options={STATUS_OPTIONS.map((s) => ({
                value: s.value,
                label: s.label,
              }))}
            />
            <DatePicker.RangePicker
              showTime
              value={range as never}
              onChange={(val) => {
                setRange(val as never);
                setPage(1);
              }}
            />
          </Space>
          <Typography.Text type="secondary">
            Total: <strong>{data?.pagination?.total ?? 0}</strong>
          </Typography.Text>
        </Flex>
      </Card>

      <Layout style={{ backgroundColor: palette.white }}>
        <Table<BotActionLogInterface>
          columns={columns}
          dataSource={data?.data ?? []}
          loading={isLoading || isFetching}
          rowKey="id"
          scroll={{ x: 1000 }}
          pagination={{
            total: data?.pagination?.total ?? 0,
            current: data?.pagination?.currentPage ?? page,
            pageSize: data?.pagination?.itemsPerPage ?? perPage,
            onChange: (newPage, newPerPage) => {
              setPage(newPage);
              if (newPerPage && newPerPage !== perPage) setPerPage(newPerPage);
            },
            showSizeChanger: true,
            pageSizeOptions: [25, 50, 100, 200],
          }}
        />
      </Layout>
    </>
  );
};
